import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tradesService } from '@/services/trades/trades-service';
import { tradesKeys } from '@/services/query-keys/trades-keys';

/**
 * LR-706 (g): what BackOffice holds for a trade's legacy posting.
 *
 * `enabled` because this is a real request to BackOffice through our API, made
 * only when an operator opens the dialog that shows it — not on every render of a
 * trade that happens to be stuck.
 *
 * `retry: false` on purpose: a refusal here comes back as a 400 whose `detail` is
 * the answer ("BackOffice holds nothing in the window we searched", "only half the
 * posting is there"). Retrying it would delay the sentence the operator needs and
 * ask BackOffice the same question three times.
 */
export const useBoPostingReadBack = (id: number, enabled: boolean) =>
  useQuery({
    queryKey: [...tradesKeys.detail(id), 'bo-posting-read-back'],
    queryFn: () => tradesService.readBackBoPosting(id),
    enabled: enabled && id > 0,
    retry: false,
    // Always ask again when the dialog reopens: the operator is about to write the
    // book from these rows, and a cached answer from ten minutes ago is exactly
    // the thing the server re-checks the uuids against.
    staleTime: 0,
    gcTime: 0,
  });

/**
 * LR-706 (g): record the posting BackOffice holds into our book.
 *
 * Invalidates the whole trades key like its siblings — this one changes more than
 * they do: the trade gains `Transaction` rows, its status moves, and the stuck
 * posting stops being reported in both the panel and the blotter.
 */
export const useRecordBoPostingFromBackoffice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, uuids }: { id: number; uuids: string[] }) =>
      tradesService.recordBoPostingFromBackoffice(id, uuids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradesKeys.all });
    },
  });
};
